<script setup>
    // Vue
    import { ref, defineEmits, defineExpose, inject } from 'vue';

    // Store
    import { useCounselingRoomGuidanceStore } from '@/stores/modal/counselingRoomGuidanceStore';
    import { useLanguageStore } from '@/stores/common/languageStore';

    // Utils
    import Utils from '@/commonProject/helpers/utils.js';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    // 親画面から取得したAPIのドメイン部を管理する。
    const API_URL = ref('');

    const languageStore = useLanguageStore();

    // ----------------------------
    // カウンセリングルーム案内
    // ----------------------------
    const counselingRoomGuidanceStore = useCounselingRoomGuidanceStore();

    /**
     * カウンセリングルーム取得処理
     * @param API_URL
     * @param queryParam
     * @returns true:正常 false:異常
     */
    async function getCounselingRoomGuidance(API_URL, queryParam) {
        const response = await counselingRoomGuidanceStore.getCounselingRoomGuidanceAction(API_URL, queryParam);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return false;
        }
        return true;
    }

    /**
     * カウンセリングルーム画像取得処理
     * @param API_URL
     * @param queryParam
     * @returns true:正常 false:異常
     */
    async function getCounselingRoomGuidanceImg(API_URL, queryParam) {
        const response = await counselingRoomGuidanceStore.getCounselingRoomGuidanceMapImageAction(API_URL, queryParam);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return false;
        }
        return true;
    }

    // ----------------------------
    // 親側実装必須処理
    // ----------------------------
    const emit = defineEmits(['counselingRoomGuidanceModal:close']);
    function close() {
        // Storeを初期化する。
        counselingRoomGuidanceStore.reset();

        // 親画面のモーダル非表示処理を呼び出す
        emit('counselingRoomGuidanceModal:close');
    }

    /**
     * 親画面からカウンセリングルームモーダルを開くイベントが実行された場合の処理。
     * @param {String} trg HTMLファイルタグが生成されている座標を取得する。
     * @param {String} roomPk 施設PK
     * @returns なし
     */
    async function openModal(trg, roomPk) {
        // Storeを初期化する。
        counselingRoomGuidanceStore.reset();

        // APIのURLを引き継ぐ
        API_URL.value = trg;

        // 施設情報取得APIのクエリパラメーターを生成する。
        const counselingRoomGuidanceQueryParam = {
            Lang: languageStore.getLanguageValue,
            RoomPk: roomPk,
        };
        // 施設情報取得APIをコールする。
        const result = await getCounselingRoomGuidance(API_URL.value, counselingRoomGuidanceQueryParam);
        if (!result) {
            return;
        }

        // 施設地図取得APIのクエリパラメーターを生成する。
        const counselingRoomGuidanceImgQueryParam = {
            RoomPk: roomPk,
        };
        // 施設地図取得APIをコールする。
        await getCounselingRoomGuidanceImg(API_URL.value, counselingRoomGuidanceImgQueryParam);

        // モーダルのHTML要素を取得する。
        const elm1 = document.querySelector('#modalCounselingRoomGuid .modalContents');

        // モーダルの縦スクロールを先頭に戻す。
        elm1.scrollTop = 0;
    }
    defineExpose({
        openModal,
    });
</script>

<style></style>

<template>
    <article id="modalCounselingRoomGuid" class="modal" :style="{ display: 'block' }">
        <h3>{{ $t('counselingRoomGuidance.parts.modalTitle1') }}</h3>
        <section class="modalContents">
            <section class="form">
                <dl class="line">
                    <dt>
                        <!--名称-->
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('counselingRoomGuidance.parts.dl1') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span>{{ counselingRoomGuidanceStore.roomName }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl class="line">
                    <dt>
                        <!--住所-->
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('counselingRoomGuidance.parts.dl2') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span>{{ counselingRoomGuidanceStore.roomAddr }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl class="line">
                    <dt>
                        <!--地図-->
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('counselingRoomGuidance.parts.dl3') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <template v-if="!Utils.isNullEmpty(counselingRoomGuidanceStore.imgFileExt)">
                                    <img :src="'data:image/' + counselingRoomGuidanceStore.imgFileExt + ';base64,' + counselingRoomGuidanceStore.roomMapImg" class="verticalMap" />
                                </template>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl class="line">
                    <dt>
                        <!--備考-->
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('counselingRoomGuidance.parts.dl4') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <p class="message minHeight134">{{ counselingRoomGuidanceStore.roomNote }}</p>
                            </li>
                        </ul>
                    </dd>
                </dl>
            </section>
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <!--閉じる-->
                    <button type="button" class="btnNormal" @click="close">{{ $t('common.parts.close') }}</button>
                </li>
            </ul>
        </section>
    </article>
</template>
